import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { TabMenu } from "primereact/tabmenu";
import { useMountEffect } from "primereact/hooks";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import Form from "../../../common/form";
import {
  departmentRequest,
  designationRequest,
  managerRequest,
  learningProgramRequest,
} from "../../../../store/slices/metaSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { sectionListRequest } from "../../../../store/slices/customFieldSlice";
import {
  createRequest,
  detailsRequest,
  updateDetailRequest,
  addAssignmentRequest,
  audienceAssignmentistRequest,
  deleteAssignmentRequest,
} from "../../../../store/slices/audienceSlice";
import AudienceDetailsForm from "./AudienceDetailsForm";
import { Button } from "primereact/button";
import { AccessScopeConstants } from "../../../../constants";
import AudienceAssignmentList from "./AudienceAssignmentList";
import { Dialog } from "primereact/dialog";
import AssignmentDetailsForm from "./AssignmentDetailsForm";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

dayjs.extend(utc);
dayjs.extend(timezone);

const AudienceDetails = ({
  action,
  addAssignmentRequest,
  assignments,
  audienceAssignmentistRequest,
  createRequest,
  customFieldSections,
  departmentRequest,
  departments,
  designationRequest,
  designations,
  detailsRequest,
  error,
  labels,
  learningProgramRequest,
  learningPrograms,
  managerRequest,
  managers,
  menu,
  record,
  requesting,
  sectionListRequest,
  updateDetailRequest,
  user,
  additionalProps = {},
  deleteAssignmentRequest,
}) => {
  const { isFromAssigment = false } = additionalProps;

  const navigate = useNavigate();
  const intl = useIntl();
  const params = useParams();
  const location = useLocation();
  const [isRecordClone, setIsRecordClone] = useState(false);
  const [activeTab, setActiveTab] = useState(isFromAssigment ? 1 : 0);
  const [isEditable, setIsEditable] = useState(params?.id ? false : true);
  const [isAssignmentDialogVisible, setIsAssignmentDialogVisible] =
    useState(false);

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.UpdateScope
  );

  useMountEffect(() => {
    departmentRequest();
    designationRequest();
    managerRequest();
    sectionListRequest();
    learningProgramRequest();
    if (params?.id) {
      detailsRequest({ id: params.id });
      audienceAssignmentistRequest({ id: params.id });
    }
    const searchParams = new URLSearchParams(location?.search);
    const cloneId = searchParams.get("clone");
    if (cloneId) {
      setIsRecordClone(true);
      detailsRequest({ id: cloneId });
    }
  });

  useEffect(() => {
    if ((action === "create" || action === "update") && !requesting && !error) {
      navigate(menu.to);
    }

    if (
      (action === "addAssignment" || action === "deleteAssignment") &&
      !requesting &&
      !error
    ) {
      audienceAssignmentistRequest({ id: record?.id });
    }
  }, [error, action, requesting, menu, navigate, audienceAssignmentistRequest]);

  const notSelectedLearningPrograms = useMemo(() => {
    return learningPrograms.filter(
      (lp) =>
        !assignments.find(
          (assignment) => assignment.learningPathwayId === lp.id
        )
    );
  }, [assignments, learningPrograms]);

  const onSubmit = (values) => {
    const payload = {
      ...values,
      data: values.data
        .filter((item) => item.value)
        .map((item, index) => ({
          ...item,
          columeName: item.columeName,
          action: item.action,
          operator: item.operator,
          order: index + 1,
          isDynamicField:
            item.columeName !== "Designation" &&
            item.columeName !== "Department" &&
            item.columeName !== "ManagerId",
        })),
    };
    if (params.id) {
      updateDetailRequest({ id: params.id, data: { ...payload } });
    } else {
      createRequest(payload);
    }
  };

  const handleAddAssignment = (values) => {
    let startDate = null;
    let endDate = null;

    if (!values?.is_flexible_date) {
      const timezone = values?.timezone;
      startDate = dayjs
        .tz(dayjs(values?.start_date).format("YYYY-MM-DD"), timezone?.id)
        .utc()
        .format();
      endDate =
        (values?.end_date &&
          dayjs
            .tz(dayjs(values?.end_date).format("YYYY-MM-DD"), timezone?.id)
            .utc()
            .format()) ||
        null;
    }

    const payload = {
      timezone: values?.timezone?.id || null,
      isDateFlexible: values?.is_flexible_date || false,
      learingPathwayId: values?.learning_program.id,
      startDate: startDate,
      endDate: endDate,
      relativeEndInDays: values?.relative_end_days || 0,
    };
    addAssignmentRequest({ id: record?.id, data: payload });
  };

  const onDeleteAssignmentAction = (data) => {
    confirmDialog({
      message: intl.formatMessage(
        { id: "delete_object_message" },
        {
          name: data.learningPathwayName,
          object: intl.formatMessage({ id: "assignment" }),
        }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        deleteAssignmentRequest({ id: data.id });
      },
      reject: () => {},
    });
  };

  return (
    <div>
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={!isRecordClone && record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({
              id: params?.id
                ? isFromAssigment
                  ? "audience_details"
                  : "edit_audience"
                : "add_audience",
            })}
          </div>
          {(params?.id &&
            !isFromAssigment &&
            !isEditable &&
            activeTab === 0 && (
              <div className="edexlms-page-actions">
                <Button
                  icon="pi pi-pencil"
                  outlined
                  onClick={() =>
                    (record?.id && setIsEditable(!isEditable)) || null
                  }
                  label={intl.formatMessage({ id: "edit" })}
                  type="button"
                  size="small"
                  disabled={!canUpdate || !record?.id}
                  tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                  tooltip={
                    canUpdate && !record?.isSystemDefined
                      ? ""
                      : intl.formatMessage({ id: "no_permission" })
                  }
                />
              </div>
            )) ||
            null}
          {(params?.id && !isEditable && activeTab === 1 && (
            <div className="edexlms-page-actions">
              <Button
                icon="pi pi-plus"
                outlined
                onClick={() =>
                  (record?.id && setIsAssignmentDialogVisible(true)) || null
                }
                label={intl.formatMessage({ id: "add_assignment" })}
                type="button"
                size="small"
                disabled={!canUpdate || !record?.id}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate && !record?.isSystemDefined
                    ? ""
                    : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div className="edexlms-page-body">
          {(params?.id && (
            <TabMenu
              activeIndex={activeTab}
              className="edexlms-tab-menu"
              model={[
                {
                  label: intl.formatMessage({ id: "basic_info" }),
                  command: () => {
                    if (activeTab !== 0) {
                      setActiveTab(0);
                      setIsEditable(false);
                    }
                  },
                },
                {
                  label: intl.formatMessage({ id: "assignments" }),
                  command: () => {
                    if (activeTab !== 1) {
                      setActiveTab(1);
                      setIsEditable(false);
                    }
                  },
                },
              ]}
            />
          )) ||
            null}
          {((activeTab === 0 || !params.id) && (
            <Form
              initialValues={{
                name: (!isRecordClone && record?.name) || "",
                data: (record?.data &&
                  record.data.length > 0 &&
                  record?.data?.map((item) => {
                    return {
                      columeName: item.columeName,
                      action: item.action,
                      operator: item.operator,
                      value: item.value,
                    };
                  })) || [
                  {
                    columeName: "",
                    action: null,
                    operator: null,
                  },
                ],
              }}
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: params?.id ? "update_audience" : "add_audience",
                }),
                onClick: onSubmit,
              }}
              cancelBtn={{
                label: intl.formatMessage({ id: "cancel" }),
                onClick: () => navigate(menu?.to),
              }}
            >
              <AudienceDetailsForm
                departments={departments}
                designations={designations}
                managers={managers}
                disabled={!isEditable}
                error={
                  action === "create" || action === "update" ? error : null
                }
                customFields={customFieldSections.reduce((acc, section) => {
                  acc = [
                    ...acc,
                    ...(section?.fields?.map((item) => ({
                      id: item.id,
                      name: item.name,
                      value: item.id,
                      type: item.type,
                      options: item?.options?.map((option) => ({
                        id: option,
                        name: option,
                        value: option,
                      })),
                    })) || []),
                  ];
                  return acc;
                }, [])}
              />
            </Form>
          )) ||
            null}
          {(activeTab === 1 && (
            <AudienceAssignmentList
              user={user}
              onDeleteAssignmentAction={onDeleteAssignmentAction}
              learningPrograms={learningPrograms}
              id={record?.id}
            />
          )) ||
            null}
        </div>
      </div>
      <div>
        <Dialog
          style={{ width: "70vw", minWidth: "400px" }}
          visible={isAssignmentDialogVisible}
          onHide={() => setIsAssignmentDialogVisible(false)}
          header={intl.formatMessage({ id: "add_assignment" })}
        >
          <Form
            initialValues={{
              learningPathwayId: record?.id,
              subject_to: 1,
              timezone: {
                id: Intl.DateTimeFormat().resolvedOptions().timeZone,
                name: Intl.DateTimeFormat().resolvedOptions().timeZone,
              },
            }}
            submitBtn={{
              label: intl.formatMessage({ id: "add_assignment" }),
              onClick: (values) => {
                handleAddAssignment(values);
                setIsAssignmentDialogVisible(false);
              },
            }}
            cancelBtn={{
              label: intl.formatMessage({ id: "cancel" }),
              onClick: () => setIsAssignmentDialogVisible(false),
            }}
          >
            <AssignmentDetailsForm progarms={notSelectedLearningPrograms} />
          </Form>
        </Dialog>
      </div>
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  action: state.audiences.action,
  customFieldSections: state?.customFields?.data || [],
  departments: state.meta.departments,
  designations: state.meta.designations,
  error: state.audiences.error,
  learningPrograms: state.meta.learningPrograms,
  managers: state.meta.managers,
  record: state.audiences.record,
  requesting: state.audiences.requesting,
  user: state.self?.user,
  assignments: state.audiences.record?.assignments || [],
});

const mapDispatchToProps = (dispatch) => ({
  departmentRequest: () => dispatch(departmentRequest()),
  designationRequest: () => dispatch(designationRequest()),
  managerRequest: () => dispatch(managerRequest()),
  sectionListRequest: () => dispatch(sectionListRequest()),
  createRequest: (data) => dispatch(createRequest(data)),
  detailsRequest: (data) => dispatch(detailsRequest(data)),
  updateDetailRequest: (data) => dispatch(updateDetailRequest(data)),
  learningProgramRequest: () => dispatch(learningProgramRequest()),
  addAssignmentRequest: (data) => dispatch(addAssignmentRequest(data)),
  audienceAssignmentistRequest: (data) =>
    dispatch(audienceAssignmentistRequest(data)),
  deleteAssignmentRequest: (data) => dispatch(deleteAssignmentRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AudienceDetails);
